// The three fields that make up a block of renderable content, shared by the
// `Section` entity and by the inline `code` page element.
//
// They live here rather than on either owner because the archived project
// learned that the hard way: both shapes there imported them from
// `site-template/fields`, and the one place they were declared is what kept an
// inline block and a stored section renderable by the same code path.
//
// `widget: "html"` / `"style"` / `"script"`, as the project this came from
// declared them — and as this file did NOT for the three weeks between the
// sections port and the code editor landing.
//
// A `widget` name is a demand, not a hint: getField() resolves a widget for
// EVERY property of a type — not only the ones a screen's `config.fields`
// names — and an unregistered name throws `widget.missing:<name>` from inside
// the render. Nothing registered those three, so declaring them took down every
// screen that touches a Section, including the LIST, which names none of these
// columns. It failed as a blank area with a console error, which is how it
// survived a build, a unit suite and a deploy and was caught by e2e.
//
// What makes them safe now is not care, it is that all three resolve:
// src/widgets/register.js registers them against @repoflow/components/code, and
// src/widgets/register.test.js pins that every name any schema in this service
// asks for is one the app registers. Adding a fourth name without adding the
// widget fails that test rather than a screen.
export const html = {
  label: "Html",
  type: "string",
  widget: "html",
  default: "",
};

export const style = {
  label: "Style",
  type: "string",
  widget: "style",
  default: "",
};

export const script = {
  label: "Script",
  type: "string",
  widget: "script",
  default: "",
};

// Declared once for the same reason: a section and a `code` element both carry
// a type declaration and the values filled into it.
export const types = {
  type: "array",
  items: { $ref: "/Types/Type" },
  default: [{ name: "root", params: [] }],
};

export const values = {
  $ref: "/Types/Value",
  default: {},
};

// The same two fields as they are STORED.
//
// `types` above bottoms out in `/Types/Param.config`, a `oneOf` selected by the
// custom `resolver` keyword. Raw jsonschema has never heard of `resolver`: it
// evaluates the `oneOf` literally, finds that several branches match (Color,
// Date and Icon are structurally identical — one optional string), and rejects
// every real value with "is not exactly one from". datorm validates writes with
// raw jsonschema (npm/pkg/@repoflow/aws/src/datorm/index.js:396), so a storage
// shape carrying the union would reject every section that has a type on it.
//
// So the storage shape says only what it can actually check. The API boundary
// does the resolver-aware pass (assertValidResolved in api/code/src/validate),
// and GraphQL already carries both of these as opaque JSON — the wire has the
// same hole for the same reason. Nothing is validated in fewer places than
// before; the check simply lives where it works.
export const storedTypes = {
  type: "array",
  items: { type: "object" },
  default: [{ name: "root", params: [] }],
};

export const storedValues = {
  type: "object",
  default: {},
};
